import { Autocomplete, TextField } from "@mui/material";
import { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CustomAttributeDefinitionModel, CustomAttributeType } from "../../models/custom-attribute.model";
import { Contact } from "../../models/conversations";
import * as selector from "../../selectors/selectors";
import { UtilityService } from "../../services/UtilityService";
import ButtonComponent from "../shared/button/button.component";
import DateAndTimePicker from "../shared/datetimepicker/date-timepicker.component";
import { emptyLabels } from "../shared/constants";
import GridComponent from "../shared/grid";
import TextFiledCellRender from "./textFieldCellRender";
import { ImportedContact, ImportedContactModelWithErrors, InvalidImportRow } from "../../models/importContacts.model";

interface IProps {
    Data: ImportedContact[];
    UploadedFile: File;
    SaveContactRow?: (row: ImportedContact, rowId?: string) => void;
    SkipContactRow?: (row: ImportedContactModelWithErrors, rowId?: string) => void;
    NewContacts?: ImportedContact[];
    ExistingContacts?: ImportedContact[];
    InValidRows?: InvalidImportRow[];
    JsonFileData: Record<string, string>[];
}


const STANDARD_IMPORT_COLUMN_LABELS: Record<string, string> = {
    FirstName: "First Name",
    LastName: "Last Name",
    PhoneNumber: "Phone Number",
    Email: "Email",
};

const normalize = (str: string) => (str || "").trim().toLowerCase();

const getDropdownOptionId = (def: CustomAttributeDefinitionModel, value: string): string => {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    const matchedOption = (def.OptionsDetail || []).find(
        (x) => x.id === trimmed || normalize(x.label) === normalize(trimmed)
    );
    return matchedOption?.id || "";
};

const getDropdownOptionLabel = (def: CustomAttributeDefinitionModel, value: string): string => {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    const matchedOption = (def.OptionsDetail || []).find((x) => x.id === trimmed);
    return matchedOption?.label || trimmed;
};

const toBooleanValue = (value: string): string => {
    const input = normalize(value);
    if (input === "true" || input === "yes") return "true";
    if (input === "false" || input === "no") return "false";
    return value;
};

const getDateTimeFromValue = (value: string): number => {
    const input = (value || "").trim();
    if (!input) return -1;
    const parsed = new Date(input);
    return Number.isNaN(parsed.getTime()) ? -1 : parsed.getTime();
};


const ExcelContactData = (props: IProps) => {
    const Contacts: Contact[] = useSelector(selector.contacts);
    const CustomAttributeDefinitions: CustomAttributeDefinitionModel[] = useSelector(selector.customAttributeFields);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode)

    const [existingContacts, setExistingContacts] = useState<ImportedContact[]>(props.ExistingContacts ?? []);
    const [newValidContacts, setNewValidContacts] = useState<ImportedContact[]>(props.NewContacts ?? []);
    const [savedInvalidContacts, setSavedInvalidContacts] = useState<ImportedContact[]>([]);
    const [skippedInvalidContacts, setSkippedInvalidContacts] = useState<ImportedContactModelWithErrors[]>([]);
    const [invalidRows, setInvalidRows] = useState<InvalidImportRow[]>(props.InValidRows ?? []);
    const invalidRowsRef = useRef<InvalidImportRow[]>(invalidRows);
    const [gridApi, setGridApi] = useState<GridApi>();

    useEffect(() => {
        invalidRowsRef.current = invalidRows;
    }, [invalidRows]);

    const customColumnsToShow = useMemo(
        () => CustomAttributeDefinitions.filter((def) => !def.IsDeleted),
        [CustomAttributeDefinitions]
    );

    const customDefinitionByReference = useMemo(() => {
        const map = new Map<string, CustomAttributeDefinitionModel>();
        customColumnsToShow.forEach((def) => map.set(def.Reference, def));
        return map;
    }, [customColumnsToShow]);

    const dropdownLookupByReference = useMemo(() => {
        const map = new Map<string, Map<string, string>>();
        customColumnsToShow.forEach((def) => {
            if (def.Type !== CustomAttributeType.Dropdown) return;
            const optionMap = new Map<string, string>();
            (def.OptionsDetail || []).forEach((option) => {
                optionMap.set(option.id, option.id);
                optionMap.set(normalize(option.label), option.id);
            });
            map.set(def.Reference, optionMap);
        });
        return map;
    }, [customColumnsToShow]);

    const existingReduxContacts = useMemo(() => {
        const phoneSet = new Set<string>();
        Contacts.forEach((contact) => {
            const rawPhone = UtilityService.unmaskPhoneNumber(contact.PhoneNumber || "");
            if (rawPhone) {
                phoneSet.add(rawPhone);
            }
        });
        return phoneSet;
    }, [Contacts]);

    const validateStandardField = useCallback((field: "FirstName" | "LastName" | "PhoneNumber" | "Email", value: string) => {
        const trimmed = (value || "").trim();
        if (field === "FirstName") {
            if (!trimmed) {
                return "First Name is required.";
            }
            if (trimmed.length > 30) return "First Name should not exceed 30 characters.";
            return "";
        }
        if (field === "LastName") {
            if (trimmed && trimmed.length > 30) return "Last Name should not exceed 30 characters.";
            return "";
        }
        if (field === "PhoneNumber") {
            const phone = UtilityService.unmaskPhoneNumber(trimmed);
            if (!phone) return "Phone Number is required.";
            if (!UtilityService.validPhoneNumber(phone) || phone.length > 15) return "Invalid Phone Number.";
            return "";
        }
        if (field === "Email") {
            if (!trimmed) return "";
            if (!UtilityService.isValidEmail(trimmed)) return "Invalid Email.";
            return UtilityService.validateEmailLength(trimmed) || "";
        }
        return "";
    }, []);

    const validateCustomField = useCallback((reference: string, rawValue: string) => {
        const columnDef = customDefinitionByReference.get(reference);
        if (!columnDef) return "";
        const value = (rawValue || "").trim();
        if (columnDef.IsRequired && !value) {
            return `${columnDef.Name} is required.`;
        }
        if (!value) return "";
        if (columnDef.Type === CustomAttributeType.Number && Number.isNaN(Number(value))) {
            return `${columnDef.Name} must be a number.`;
        }
        if (columnDef.Type === CustomAttributeType.Date) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return `${columnDef.Name} must be a valid date.`;
            }
        }
        if (columnDef.Type === CustomAttributeType.Boolen) {
            const parsed = toBooleanValue(value);
            if (parsed !== "true" && parsed !== "false") {
                return `${columnDef.Name} must be true or false.`;
            }
        }
        if (columnDef.Type === CustomAttributeType.Dropdown) {
            const optionId = dropdownLookupByReference.get(reference)?.get(value) || dropdownLookupByReference.get(reference)?.get(normalize(value)) || "";
            if (!optionId) {
                return `${columnDef.Name} has an invalid option.`;
            }
        }
        return "";
    }, [customDefinitionByReference, dropdownLookupByReference]);

    const validateRow = useCallback((row: InvalidImportRow): Record<string, string> => {
        const Errors: Record<string, string> = {};
        const firstNameError = validateStandardField("FirstName", row.FirstName || "");
        const lastNameError = validateStandardField("LastName", row.LastName || "");
        const phoneError = validateStandardField("PhoneNumber", row.PhoneNumber || "");
        const emailError = validateStandardField("Email", row.Email || "");
        if (firstNameError) Errors.FirstName = firstNameError;
        if (lastNameError) Errors.LastName = lastNameError;
        if (phoneError) Errors.PhoneNumber = phoneError;
        if (emailError) Errors.Email = emailError;
        customColumnsToShow.forEach((def) => {
            const customError = validateCustomField(def.Reference, row.customValues[def.Reference] || "");
            if (customError) {
                Errors[`custom:${def.Reference}`] = customError;
            }
        });

        return Errors;
    }, [customColumnsToShow, validateCustomField, validateStandardField]);

    const getInvalidRowById = useCallback((rowId: string) => {
        return invalidRowsRef.current.find((r) => r.RowId === rowId);
    }, []);

    const toImportedContact = useCallback((row: InvalidImportRow): ImportedContact => {
        const CustomAttributes = Object.entries(row.customValues || {})
            .map(([reference, rawValue]) => {
                const def = customDefinitionByReference.get(reference);
                if (!def) return null;
                const value = (rawValue || "").trim();
                if (!value) return null;
                const formattedValue = def.Type === CustomAttributeType.Dropdown
                    ? (dropdownLookupByReference.get(def.Reference)?.get(value) || dropdownLookupByReference.get(def.Reference)?.get(normalize(value)) || value)
                    : def.Type === CustomAttributeType.Boolen ? toBooleanValue(value) : value;

                if (!formattedValue) return null;

                return {
                    id: def.Id || "",
                    reference: reference,
                    name: def.Name,
                    value: formattedValue,
                    type: def.Type
                };
            })
            .filter((x) => !!x);
        return {
            FirstName: (row.FirstName || "").trim(),
            LastName: (row.LastName || "").trim(),
            Email: (row.Email || "").trim(),
            PhoneNumber: UtilityService.unmaskPhoneNumber(row.PhoneNumber || ""),
            CustomAttributes,
        };
    }, [customDefinitionByReference, dropdownLookupByReference]);

    const toImportedContactModelWithErrors = useCallback(
        (row: InvalidImportRow): ImportedContactModelWithErrors => {
            const base = toImportedContact(row);
            const Errors: { columnName: string; errorMessage: string; }[] = Object.entries(row.Errors || {}).map(
                ([errorKey, errorMessage]) => {
                    let columnName: string;
                    if (errorKey.startsWith("custom:")) {
                        const ref = errorKey.slice("custom:".length);
                        columnName = customDefinitionByReference.get(ref)?.Name ?? ref;
                    } else {
                        columnName = STANDARD_IMPORT_COLUMN_LABELS[errorKey] ?? errorKey;
                    }
                    return { columnName, errorMessage };
                }
            );
            return { ...base, Errors };
        },
        [customDefinitionByReference, toImportedContact]
    );

    const updateInvalidRow = useCallback((rowId: string, updater: (row: InvalidImportRow) => InvalidImportRow) => {
        setInvalidRows((prev) => {
            const next = prev.map((row) => {
                if (row.RowId !== rowId) return row;
                const updated = updater(row);
                return { ...updated, Errors: validateRow(updated) };
            });
            invalidRowsRef.current = next;
            return next;
        });
    }, [validateRow]);

    const getCellError = useCallback((row: InvalidImportRow, field: string, customReference?: string) => {
        if (customReference) {
            return row.Errors[`custom:${customReference}`] || "";
        }
        return row.Errors[field] || "";
    }, []);

    const renderEditableField = useCallback((params: ICellRendererParams, field: keyof InvalidImportRow) => {
        const row = params.data as InvalidImportRow;
        const value = (params.value || "").toString();
        const error = getCellError(row, field.toString());
        const fieldName = field.toString();

        const applyField = (nextValue: string) =>
            updateInvalidRow(row.RowId, (current) => ({
                ...current,
                [field]: nextValue,
            }));

        return (
            <TextFiledCellRender
                Value={value}
                Error={error}
                OnCommit={applyField}
                OnLiveRowUpdate={applyField}
                OnValidateWhileTyping={(nextValue) => {
                    return validateStandardField(fieldName as "FirstName" | "LastName" | "PhoneNumber" | "Email", nextValue);
                }}
                Type={field === "PhoneNumber" ? "text" : "text"}
            />
        );
    }, [getCellError, updateInvalidRow, validateStandardField]);

    const renderCustomField = useCallback((params: ICellRendererParams, def: CustomAttributeDefinitionModel) => {
        const row = params.data as InvalidImportRow;
        const rawValue = (row.customValues[def.Reference] || "").toString();
        const value = def.Type === CustomAttributeType.Dropdown ? getDropdownOptionId(def, rawValue) || rawValue : rawValue;
        const error = getCellError(row, "", def.Reference);

        const onChangeValue = (nextValue: string) => {
            updateInvalidRow(row.RowId, (current) => ({
                ...current,
                customValues: {
                    ...current.customValues,
                    [def.Reference]: nextValue,
                },
            }));
        };

        if (def.Type === CustomAttributeType.Boolen) {
            const options = ["true", "false"];
            const normalized = toBooleanValue(value);
            const selected = options.includes(normalized) ? normalized : null;

            return (
                <div className="w-p-100 h-p-100 d-flex flex-direction">
                    <div className="d-flex align-center justify-center h-p-100 w-p-100">
                        <Autocomplete
                            fullWidth
                            sx={{ width: "100%" }}
                            size="small"
                            options={options}
                            value={selected}
                            onChange={(_, nextValue) => onChangeValue(nextValue || "")}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    error={!!error}
                                    placeholder={"Select"}
                                    InputProps={{
                                        ...params.InputProps,
                                        classes: { input: "h6-regular" },
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="d-flex align-center justify-flex-end h6-regular color-primary-danger t-align-right">
                        {error || "\u00A0"}
                    </div>
                </div>
            );
        }

        if (def.Type === CustomAttributeType.Dropdown) {
            const options = (def.OptionsDetail || [])
            const selected = options.find((option) => option.id === value) || null;
            return (
                <div className="w-p-100 h-p-100 d-flex flex-direction">
                    <div className="d-flex align-center justify-center h-p-100 w-p-100">
                        <Autocomplete
                            fullWidth
                            sx={{ width: "100%" }}
                            size="small"
                            options={options}
                            value={selected}
                            onChange={(_, nextValue) => onChangeValue(nextValue?.id || "")}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, selectedOption) => option.id === selectedOption.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    error={!!error}
                                    placeholder={"Select"}
                                    InputProps={{
                                        ...params.InputProps,
                                        classes: { input: "h6-regular" },
                                    }}
                                />
                            )}
                        />
                    </div>
                    <div className="d-flex align-center justify-flex-end h6-regular color-primary-danger t-align-right">
                        {error || "\u00A0"}
                    </div>
                </div>
            );
        }

        if (def.Type === CustomAttributeType.Date) {
            return (
                <div className="w-p-100 h-p-100 d-flex flex-direction">
                    <div className="d-flex align-center justify-center h-p-100">
                        <DateAndTimePicker
                            DateTime={getDateTimeFromValue(value)}
                            HideTime={true}
                            ShowMinData={true}
                            FormatText="MMMM Do YYYY"
                            FormatTextLabel="Select date"
                            handleChange={(dateTime: number) => {
                                if (dateTime === -1) {
                                    onChangeValue("");
                                    return;
                                }
                                onChangeValue(new Date(dateTime).toISOString().slice(0, 10));
                            }}
                        />
                    </div>
                    <div
                        className="d-flex align-center justify-flex-end h6-regular color-primary-danger mb-1 t-align-right"
                        style={{ width: "100%" }}
                    >
                        {error || "\u00A0"}
                    </div>
                </div>
            );
        }

        return (
            <TextFiledCellRender
                Value={value}
                Error={error}
                OnCommit={onChangeValue}
                OnLiveRowUpdate={onChangeValue}
                OnValidateWhileTyping={(nextValue) => {
                    return validateCustomField(def.Reference, nextValue);
                }}
                Type={def.Type === CustomAttributeType.Number ? "number" : "text"}
            />
        );
    }, [getCellError, updateInvalidRow, validateCustomField]);

    const saveRow = useCallback((row: InvalidImportRow) => {
        const savedContact = toImportedContact(row);
        const normalizedPhone = UtilityService.unmaskPhoneNumber(savedContact.PhoneNumber || "");
        if (normalizedPhone && existingReduxContacts.has(normalizedPhone)) {
            setExistingContacts((prev) => [...prev, savedContact])
        } else {
            setNewValidContacts((prev) => [...prev, savedContact]);
        }
        setSavedInvalidContacts((prev) => [...prev, savedContact]);
        setInvalidRows((prev) => prev.filter((x) => x.RowId !== row.RowId));
        props.SaveContactRow?.(savedContact, row.RowId);
    }, [existingReduxContacts, toImportedContact, props.SaveContactRow]);

    const skipRow = useCallback((row: InvalidImportRow) => {
        const skippedContact = toImportedContactModelWithErrors(row)
        setSkippedInvalidContacts((prev) => [...prev, skippedContact]);
        setInvalidRows((prev) => prev.filter((x) => x.RowId !== row.RowId));
        props.SkipContactRow(skippedContact, row.RowId)
    }, [toImportedContact]);

    const columnDefs: ColDef[] = useMemo(() => {
        const baseColumns: ColDef[] = [
            {
                field: "FirstName",
                headerName: "First Name",
                minWidth: 200,
                autoHeight: true,
                cellRenderer: (params: ICellRendererParams) => renderEditableField(params, "FirstName"),
                cellStyle: { display: "flex", alignItems: "center" },
                cellClass: "my-1"
            },
            {
                field: "LastName",
                headerName: "Last Name",
                minWidth: 200,
                autoHeight: true,
                cellRenderer: (params: ICellRendererParams) => renderEditableField(params, "LastName"),
                cellStyle: { display: "flex", alignItems: "center" },
                cellClass: "my-1"
            },
            {
                field: "PhoneNumber",
                headerName: "Phone Number",
                minWidth: 200,
                autoHeight: true,
                cellRenderer: (params: ICellRendererParams) => renderEditableField(params, "PhoneNumber"),
                cellStyle: { display: "flex", alignItems: "center" },
                cellClass: "my-1"
            },
            {
                field: "Email",
                headerName: "Email",
                minWidth: 220,
                autoHeight: true,
                cellClass: "my-1",
                cellRenderer: (params: ICellRendererParams) => renderEditableField(params, "Email"),
                cellStyle: { display: "flex", alignItems: "center" },
            },
        ];

        const customColumns: ColDef[] = customColumnsToShow.map((def) => ({
            field: `custom_${def.Reference}`,
            headerName: def.Name,
            minWidth: 200,
            valueGetter: (params) => {
                const raw = params.data?.customValues?.[def.Reference] || "";
                return def.Type === CustomAttributeType.Dropdown ? getDropdownOptionLabel(def, raw) : raw;
            },
            autoHeight: true,
            cellClass: "my-1",
            cellRenderer: (params: ICellRendererParams) => renderCustomField(params, def),
            cellStyle: { display: "flex", alignItems: "center" },
        }));

        return [
            ...baseColumns,
            ...customColumns,
            {
                headerName: "Actions",
                maxWidth: 170,
                pinned: "right",
                autoHeight: true,
                valueGetter: (params) =>
                    JSON.stringify((params.data as InvalidImportRow | undefined)?.Errors ?? {}),
                cellRenderer: (params: ICellRendererParams) => {
                    const gridRow = params.data as InvalidImportRow;
                    const row = getInvalidRowById(gridRow?.RowId) ?? gridRow;
                    const hasErrors = Object.keys(row?.Errors || {}).length > 0;
                    return (
                        <div className="d-flex align-center">
                            <ButtonComponent
                                ButtonLabel="Save"
                                Variant={hasErrors ? "disabled-btn-small" : "primary-btn-small"}
                                ClassName="mr-1"
                                OnClick={(e) => {
                                    e.stopPropagation();
                                    (document.activeElement as HTMLElement)?.blur?.();
                                    const latestRow = getInvalidRowById(gridRow?.RowId) ?? gridRow;
                                    if (!latestRow || Object.keys(latestRow.Errors || {}).length > 0) return;
                                    saveRow(latestRow);
                                }}
                            />
                            <ButtonComponent
                                ButtonLabel="Skip"
                                Variant="secondary-btn-small"
                                OnClick={(e) => {
                                    e.stopPropagation();
                                    const latestRow = getInvalidRowById(gridRow?.RowId) ?? gridRow;
                                    if (latestRow) skipRow(latestRow);
                                }}
                            />
                        </div>
                    );
                },
                cellStyle: { display: "flex", alignItems: "center" },
            },
        ];
    }, [customColumnsToShow, getInvalidRowById, renderCustomField, renderEditableField, saveRow, skipRow]);

    const onGridReady = useCallback((params: any) => {
        setGridApi(params.api);
    }, []);

    return (
        <div className="bg-color-secondary w-p-100 br-5 mx-auto">
            <div className=" w-p-95 mx-auto mt-3">
                <div className="h4-regular color-menu-list-font-subnav mb-1">
                    What we found in {props?.UploadedFile?.name}
                </div>
                <div className="h5-regular w-fit">
                    {(props.JsonFileData?.length !== existingContacts.length && !!newValidContacts.length) && <div className="bg-color-secure-file-active p-1 br-2 my-1 border-primary-divider d-flex align-center color-primary-success">
                        <div className="py-1 px-2 br-1 bg-color-secondary h4-medium">+</div>
                        <div className="ml-1">
                            <div className="d-flex align-center">
                                <span className="h1-medium">{newValidContacts.length}</span>
                                <span className="h5-medium ml-1">New contacts ready to import</span>
                            </div>
                            <div className="h5-medium">These phone numbers aren’t in Falkon yet — they’ll be added.</div>
                        </div>
                    </div>
                    }

                    {!!existingContacts.length && <div className={`bg-color-con-hist-outbound-msg-bg p-1 br-2 my-1 border-primary-divider d-flex align-center  ${IsDarkMode ? "color-lite-dark-white" : "color-primary-link"}`}>
                        <div className="py-1 px-2 br-1 bg-color-secondary h4-medium">~</div>
                        <div className="ml-1">
                            <div className="d-flex align-center">
                                <span className="h1-medium">{existingContacts.length}</span>
                                <span className="h5-medium ml-1">Already in Falkon</span>
                            </div>
                            <div className="h5-medium">We found matching phone numbers — you’ll choose how to merge next.</div>
                        </div>
                    </div>}

                    {(props.JsonFileData?.length !== existingContacts.length && !!invalidRows.length) && <div className="bg-color-photosensitivity-warning p-1 br-2 my-1 border-primary-divider d-flex align-center color-secure-file-Expier">
                        <div className="py-1 px-2 br-1 bg-color-secondary h4-medium">!</div>
                        <div className="ml-1">
                            <div className="d-flex align-center">
                                <span className="h1-medium">{invalidRows.length}</span>
                                <span className="h5-medium ml-1">Invalid phone numbers</span>
                            </div>
                            <div className="h5-medium">Fix them inline below, or skip and continue.</div>
                        </div>
                    </div>
                    }
                    {props.JsonFileData?.length !== existingContacts.length && <div className="bg-color-report-background p-1 br-2 my-1 border-primary-divider d-flex align-center color-secure-file-Expier justify-between h5-medium">
                        <span className="color-menu-list-font-subnav">Total valid contacts that will be imported:</span>
                        <span className="color-primary-accent h2-medium">{newValidContacts?.length + existingContacts?.length}</span>
                    </div>
                    }
                </div>

                {props.JsonFileData?.length === existingContacts.length &&
                    <div className='d-flex align-center bg-color-secure-file-active my-2 py-1 br-2'>
                        <div className='w-4 h-4 br-50 color-secondary h3-medium bg-color-primary-success d-flex align-center justify-center ml-1'> <span>✓</span></div>
                        <div className='color-primary-success ml-1'>
                            <div className='h5-medium'>All columns matched automatically — nothing to do here.</div>
                        </div>
                    </div>
                }

                {(props.JsonFileData?.length !== existingContacts.length && !!invalidRows.length) && <div>
                    <div className="h4-regular color-primary-light mt-1 mb-1">
                        <b>Invalid format contacts (editable)</b>
                    </div>
                    <div className="h6-regular mb-1">
                        All invalid rows open in editable input mode. Fix errors and click Save, or click Skip to ignore a row.
                    </div>

                    <div className="mb-1 h-45">
                        <GridComponent
                            ColumnDefs={columnDefs}
                            RowData={invalidRows}
                            OnGridReady={onGridReady}
                            IsPaginationEnabled={true}
                            EmptyLabel={emptyLabels.CONTACTS}
                            LockVisible={false}
                            isHorizontalScroll={true}
                            GetRowId={(p) => String((p.data as InvalidImportRow).RowId)}
                            EnableCellChangeFlash={false}
                            RowHeight={65}
                        />
                    </div>
                </div>}
            </div>
        </div>
    );
};

export default ExcelContactData;